<template>
    <app-layout title="Settings">
        <!-- Password Modal -->
        <jet-dialog-modal :show="requiresPassword" @close="handlePasswordCancel">
            <template #title>
                <h3 class="text-2xl">Enter Password</h3>
            </template>

            <template #content>
                <div class="my-6">
                    <div class="mb-4">
                        <jet-label value="Password:" class="mb-2" />
                        <jet-input 
                            v-model="passwordForm.password" 
                            type="password" 
                            class="w-full"
                            @keyup.enter="verifyPassword"
                            ref="passwordInput"
                            placeholder="Enter password to access promo codes"
                        />
                        <jet-input-error :message="passwordForm.errors.password" />
                        <jet-input-error :message="passwordForm.errors.general" />
                    </div>
                </div>
            </template>

            <template #footer>
                <jet-button @click="verifyPassword" :disabled="passwordForm.processing">
                    Verify
                </jet-button>
                <jet-tertiary-button @click="handlePasswordCancel" class="ml-2">
                    Cancel
                </jet-tertiary-button>
            </template>
        </jet-dialog-modal>

        <!-- Main Content (only shown after password verification) -->
        <div v-if="!requiresPassword">
            <form @submit.prevent="submit">
                <page-header>
                    <template #title>Settings - Promo Codes</template>

                    <template #actions>
                        <jet-button type="button" class="h-7 leading-none" @click="openAddModal" >
                            Add Promo Code
                        </jet-button>
                    </template>
                </page-header>

                <settings-navigation />

                <div class="flex px-8">
                    <div class="flex gap-x-2 w-3/4">
                        <div class="flex flex-col flex-1">
                            <jet-input type="text" class="h-7" name="search" v-model="mainForm.search" @input="handleSubmit" placeholder="Search..."/>
                        </div>
                        <div class="flex flex-col w-40">
                            <multiselect
                                v-model="mainForm.status"
                                :options="statusOptions"
                                :canDeselect="false"
                                :canClear="false"
                                :classes="{ singleLabel: 'w-full px-2' }"
                                class="h-8 multi-sm multi-custom text-base"
                                openDirection="bottom"
                                @change="onStatusFilterChange"
                            />
                        </div>
                        <div class="flex flex-col w-40">
                            <multiselect
                                v-model="mainForm.discountType"
                                :options="discountTypeOptions"
                                :canDeselect="false"
                                :canClear="false"
                                :classes="{ singleLabel: 'w-full px-2' }"
                                class="h-8 multi-sm multi-custom text-base"
                                openDirection="bottom"
                                @change="handleSubmit"
                            />
                        </div>
                        <div class="flex flex-col w-48">
                            <multiselect
                                v-model="mainForm.usageType"
                                :options="usageTypeOptions"
                                :canDeselect="false"
                                :canClear="false"
                                :classes="{ singleLabel: 'w-full px-2' }"
                                class="h-8 multi-sm multi-custom text-base"
                                openDirection="bottom"
                                @change="handleSubmit"
                            />
                        </div>
                        <div v-if="statusFilterValue === 'inactive'" class="flex flex-col w-48">
                            <multiselect
                                v-model="mainForm.inactive_subfilter"
                                :options="inactiveSubfilterOptions"
                                :canDeselect="false"
                                :canClear="false"
                                :classes="{ singleLabel: 'w-full px-2' }"
                                class="h-8 multi-sm multi-custom text-base"
                                openDirection="bottom"
                                @change="handleSubmit"
                            />
                        </div>
                        <div class="self-end">
                            <jet-tertiary-button type="button" class="py-1 h-7" @click="handleReset" >
                                Reset
                            </jet-tertiary-button>
                        </div>
                    </div>
                </div>

                <div class="px-8 mt-2">
                    <div class="rounded-t overflow-hidden border border-b-0 w-3/4">
                        <table class="table w-full">
                            <thead>
                                <tr>
                                    <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="is_active">Status</sortable-header>
                                    <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="code">Code</sortable-header>
                                    <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="type">Type</sortable-header>
                                    <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="discount_amount">Discount</sortable-header>
                                    <sortable-header class="text-right" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="times_used">Times used</sortable-header>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="code in codes.data" :key="code.id"
                                    :class="{
                                        'cursor-pointer': true,
                                        'text-gray-400': code.customer_id !== null,
                                    }"
                                    @click="editPromoCode(code)"
                                >
                                    <td class="text-left">
                                        <Pill :color="code.is_active ? 'primary-green' : 'primary-red'" size="sm" variant="soft">{{ code.display_status }}</Pill>
                                    </td>
                                    <td class="text-left">
                                        {{ code.code }}
                                    </td>
                                    <td class="text-left">
                                        {{ code.type === 'prc_discount' ? '%' : '$' }} Discount
                                    </td>
                                    <td class="text-left">
                                        {{ code.type === 'dollar_discount' ? '$ ' : '&nbsp;&nbsp;' }}{{ Number(code.discount_amount) }}{{ code.type === 'prc_discount' ? ' %' : '' }}
                                    </td>
                                    <td class="text-right">
                                        {{ code.times_used }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="flex justify-between items-center mt-4 w-3/4">
                        <pagination class="mt-0 mb-0" :links="codes.links" />

                        <div class="w-40">
                            <multiselect
                                v-model.number="mainForm.paginate"
                                :options="paginateOptions"
                                :canDeselect="false"
                                :canClear="false"
                                :classes="{ singleLabel: 'w-full px-2' }"
                                class="h-8 multi-sm multi-custom text-base"
                                openDirection="top"
                                @change="handleSubmit"
                            />
                        </div>
                    </div>
                </div>

                <div class="px-8 mt-2 text-sm">
                    <div class="font-semibold mb-1">Unredeemed Gift Cards</div>
                    <div class="flex flex-col gap-y-0.5">
                        <div class="flex flex-row flex-nowrap items-baseline gap-x-2 text-gray-500">
                            <span class="min-w-[9.5rem] shrink-0">Active Amount:</span>
                            <span class="tabular-nums">$ {{ unredeemed_gift_card_active_amount }}</span>
                        </div>
                        <div class="flex flex-row flex-nowrap items-baseline gap-x-2 text-gray-500">
                            <span class="min-w-[9.5rem] shrink-0">Inactive Amount:</span>
                            <span class="tabular-nums">$ {{ unredeemed_gift_card_inactive_amount }}</span>
                        </div>
                        <div class="flex flex-row flex-nowrap items-baseline gap-x-2 text-gray-800 font-semibold">
                            <span class="min-w-[9.5rem] shrink-0">Total Amount:</span>
                            <span class="tabular-nums">$ {{ unredeemed_gift_card_total_amount }}</span>
                        </div>
                    </div>
                </div>

                <!-- Add/Edit Promo Code -->
                <jet-dialog-modal :show="showModal" @close="closeModal">
                    <template #title>
                         <h3 class="text-2xl">{{ showModal == 'add' ? 'Add' : 'Edit' }} Promo Code</h3>
                    </template>

                    <template #content>
                        <form @submit.prevent="submitForm" class="my-6">
                            <div class="flex gap-x-4 items-center mb-4">
                                <jet-label value="Status:" class="mb-0" style="min-width: 90px;" />
                                <span class="text-gray-900 leading-7">{{ form.display_status }}</span>
                            </div>

                            <div class="flex gap-x-4 items-center mb-4">
                                <jet-label value="Code:" class="mb-0" style="min-width: 90px;" />
                                <jet-input v-model="form.code" type="text" class="w-60"/>
                                <jet-input-error :message="form.errors.code" />
                            </div>

                            <div v-if="showModal === 'add'" class="flex gap-x-4 items-start mb-4">
                                <jet-label value="Created for:" class="mb-0 pt-1" style="min-width: 90px;" />
                                <div class="flex flex-col flex-1">
                                    <jet-input v-model="form.created_for" type="text" class="w-full max-w-md" placeholder="Who or what this code is for"/>
                                    <jet-input-error :message="form.errors.created_for" />
                                </div>
                            </div>

                            <div v-if="showModal === 'edit' && !form.deleted_at" class="flex gap-x-4 items-start mb-4">
                                <jet-label value="Created for:" class="mb-0 pt-1" style="min-width: 90px;" />
                                <div class="flex flex-col flex-1">
                                    <jet-input v-model="form.created_for" type="text" class="w-full max-w-md"/>
                                    <jet-input-error :message="form.errors.created_for" />
                                </div>
                            </div>

                            <div v-if="showModal === 'edit' && form.deleted_at && form.created_for" class="flex gap-x-4 items-center mb-4">
                                <jet-label value="Created for:" class="mb-0" style="min-width: 90px;" />
                                <span class="text-gray-900 leading-7 max-w-md">{{ form.created_for }}</span>
                            </div>

                            <div class="flex gap-x-4 items-center mb-4">
                                <jet-label value="Discount:" class="mb-0" style="min-width: 90px;" />
                                <div class="flex flex-col items-start w-32">
                                    <div class="flex flex-row items-center mb-2">
                                        <jet-input v-model="form.type" type="radio" value="prc_discount" name="type" id="prc_discount" :checked="form.type === 'prc_discount'"/>
                                        <jet-label value="% Discount" class="mb-0 ml-2" style="width: 90px;" for="prc_discount"/>
                                    </div>
                                    <div class="flex flex-row items-center">
                                        <jet-input v-model="form.type" type="radio" value="dollar_discount" name="type" id="dollar_discount" :checked="form.type === 'dollar_discount'"/>
                                        <jet-label value="$ Discount" class="mb-0 ml-2" style="width: 90px;" for="dollar_discount"/>
                                    </div>
                                </div>
                                <div class="flex flex-row items-center">
                                    <span v-if="form.type === 'dollar_discount'" style="height: 34px;"
                                        class="pt-1 px-3 border border-r-0 rounded-r-none rounded-l"
                                    >$</span>
                                    <jet-input type="number" class="w-20" v-model="form.discount_amount"
                                        :class="{
                                            'rounded-l-none': form.type === 'dollar_discount',
                                            'rounded-r-none': form.type === 'prc_discount',
                                        }"
                                    />
                                    <span v-if="form.type === 'prc_discount'" style="height: 34px;"
                                        class="pt-1 px-3 border border-l-0 rounded-l-none rounded-r"
                                    >%</span>
                                </div>
                                <div class="flex flex-col items-left">
                                    <jet-input-error :message="form.errors.type" />
                                    <jet-input-error :message="form.errors.discount_amount" />
                                </div>
                            </div>

                            <div class="flex gap-x-4 items-start">
                                <jet-label value="Usage:" class="mb-0" style="min-width: 90px;" />
                                <div class="flex flex-col items-start w-60">
                                    <div class="flex flex-row items-center mb-2">
                                        <jet-input v-model="form.usage" type="radio" value="1" name="usage" id="usage_once" :checked="form.usage === 1"/>
                                        <jet-label value="One Time" class="mb-0 ml-2" for="usage_once"/>
                                    </div>
                                    <div class="flex flex-row items-center mb-2">
                                        <jet-input v-model="form.usage" type="radio" value="0" name="usage" id="usage_unlimited" :checked="form.usage === 0"/>
                                        <jet-label value="Unlimited" class="mb-0 ml-2" for="usage_unlimited"/>
                                    </div>
                                    <div class="flex flex-row items-center mb-2">
                                        <jet-input v-model="form.usage" type="radio" value="-1" name="usage" id="usage_once_per_customer" :checked="form.usage === -1"/>
                                        <jet-label value="Once Per Customer" class="mb-0 ml-2" for="usage_once_per_customer"/>
                                    </div>
                                    <div class="flex flex-row items-center">
                                        <jet-input v-model="form.usage" type="radio" value="2" name="usage" id="usage_once_only_new_customer" :checked="form.usage === 2"/>
                                        <jet-label value="New customers only" class="mb-0 ml-2" for="usage_once_only_new_customer"/>
                                    </div>
                                </div>
                                <jet-input-error :message="form.errors.usage" />
                            </div>

                            <div class="flex gap-x-4 items-center mt-4">
                                <jet-label value="Employee code:" class="mb-0" style="min-width: 90px;" />
                                <toggle v-model="form.is_employee_code"/>
                                <jet-input-error :message="form.errors.is_employee_code" />
                            </div>

                            <div v-if="!form.deleted_at" class="flex gap-x-4 items-center mt-4">
                                <jet-label value="Active:" class="mb-0" style="min-width: 90px;" />
                                <toggle v-model="form.is_active"/>
                                <jet-input-error :message="form.errors.is_active" />
                            </div>

                            <div v-if="showModal === 'edit'" class="mt-4">
                                <div v-if="form.creation_type" class="flex gap-x-4 items-center mb-4">
                                    <jet-label value="Creation type:" class="mb-0" style="min-width: 90px;" />
                                    <span class="text-gray-900 leading-7">{{ formatCreationTypeLabel(form.creation_type) }}</span>
                                </div>
                                <div v-if="form.created_by_user" class="flex gap-x-4 items-center mb-4">
                                    <jet-label value="Created by:" class="mb-0" style="min-width: 90px;" />
                                    <span class="text-gray-900 leading-7">{{ form.created_by_user.name }}</span>
                                </div>
                                <div v-if="form.orig_discount_amount != null" class="flex gap-x-4 items-center mb-4">
                                    <jet-label value="Original discount:" class="mb-0" style="min-width: 90px;" />
                                    <span class="text-gray-900 leading-7">{{ formatOriginalDiscountDisplay(form) }}</span>
                                </div>
                                <div v-if="showRemainingBalanceLine" class="flex gap-x-4 items-center mb-4">
                                    <jet-label value="Remaining balance:" class="mb-0" style="min-width: 90px;" />
                                    <span class="text-gray-900 leading-7">${{ Number(form.discount_amount).toFixed(2) }}</span>
                                </div>
                                <div class="flex gap-x-4 items-center mb-4">
                                    <jet-label value="Times used:" class="mb-0" style="min-width: 90px;" />
                                    <span class="text-gray-900 leading-7">{{ form.times_used }}</span>
                                </div>
                            </div>

                            <div v-if="showModal == 'edit' && !form.deleted_at" class="flex gap-x-4 items-start mt-4" >
                                <jet-label value="Order history:" class="mb-0" style="min-width: 90px;" />
                                <div v-if="form.loading_outlet_orders">
                                    <img src="/images/icons/loading-pulse.svg" height="64" width="64" class="-mt-3" />
                                </div>
                                <div v-else-if="!form.outlet_orders.length" class="text-muted">
                                    No Related Orders
                                </div>
                                <div v-else class="flex flex-col">
                                    <div v-for="order, index in form.outlet_orders" :key="order.id">
                                        {{ `${index + 1}. ` }}
                                        <Link :href="orderUrl(order.id)" preserve-scroll class="underline text-indigo-700" @click.stop>
                                            {{ order.order_number }} ({{ formatOrderPromoDiscount(order.promo_code_amount) }} discounted)
                                        </Link>
                                    </div>
                                    <div v-if="outletOrdersRemaining > 0" class="mt-2">
                                        <div class="text-muted text-sm">...and {{ outletOrdersRemaining }} more orders</div>
                                        <button class="text-indigo-700 underline text-sm" @click="loadPromoCodeOrders(true)">Show all</button>
                                    </div>
                                </div>
                            </div>

                            <input type="submit" class="hidden" ref="submitFormButton" />
                        </form>
                    </template>

                    <template #footer>
                        <jet-danger-button v-if="showModal === 'edit' && !form.deleted_at" class="mr-2" @click="deletePromoCode(form)">
                            Delete
                        </jet-danger-button>

                        <jet-button v-if="!form.deleted_at" class="mr-2" @click="$refs.submitFormButton.click()" :disabled="form.processing">
                            Save
                        </jet-button>

                        <jet-tertiary-button @click="closeModal">
                            Cancel
                        </jet-tertiary-button>
                    </template>
                </jet-dialog-modal>

                <!-- Delete Promo Code -->
                <jet-confirmation-modal :show="showDeleteModal" @close="showDeleteModal = false">
                    <template #title>
                        Delete Promo Code
                    </template>

                    <template #content>
                        Are you sure you would like to delete <strong>{{ form.code }}</strong>?
                    </template>

                    <template #footer>
                        <jet-danger-button @click="handleDeletePromoCode">
                            Yes
                        </jet-danger-button>

                        <jet-tertiary-button class="ml-2" @click="showDeleteModal = false">
                            No
                        </jet-tertiary-button>
                    </template>
                </jet-confirmation-modal>

            </form>
        </div>
    </app-layout>
</template>

<script>
import { Link, useForm } from '@inertiajs/inertia-vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetLabel from '@/Jetstream/Label';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError.vue';
import JetDialogModal from '@/Jetstream/DialogModal';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import JetButton from '@/Jetstream/Button';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import SettingsNavigation from '@/Pages/Settings/Partials/SettingsNavigation';
import Multiselect from '@vueform/multiselect';
import Pagination from '@/Jetstream/Pagination'
import SortableHeader from '@/Shared/SortableHeader';
import Pill from '@/Jetstream/Pill.vue';
import Toggle from '@vueform/toggle';

import '@vueform/toggle/themes/default.css';
import '@vueform/multiselect/themes/default.css';

export default {
    setup(props) {
        const mainForm = useForm({
            paginate: props.paginate,
            search: props.search,
            sortColumn: props.sortColumn,
            sortDirection: props.sortDirection,
            status: props.status,
            inactive_subfilter: props.inactiveSubfilter || 'all',
            discountType: props.discountType,
            usageType: props.usageType ?? 'all',
        });

        const form = useForm({
            id: null,
            code: '',
            type: '',
            discount_amount: '',
            usage: '',
            is_employee_code: false,
            is_active: true,
            created_for: '',
            creation_type: null,
            orig_discount_amount: null,
            times_used: 0,
            display_status: '',
            created_by_user: null,
            deleted_at: null,
            loading_outlet_orders: false,
            outlet_orders: [],
            outlet_orders_total: 0,
        });

        const passwordForm = useForm({
            password: '',
        });

        return { mainForm, form, passwordForm }
    },

    components: {
        Link,
        AppLayout,
        PageHeader,
        JetLabel,
        JetInput,
        JetInputError,
        JetDialogModal,
        JetConfirmationModal,
        JetButton,
        JetDangerButton,
        JetTertiaryButton,
        SettingsNavigation,
        Multiselect,
        Pagination,
        SortableHeader,
        Pill,
        Toggle,
    },

    props: ['codes', 'search', 'sortColumn', 'sortDirection', 'paginate', 'status', 'inactiveSubfilter', 'discountType', 'usageType', 'unredeemed_gift_card_active_amount', 'unredeemed_gift_card_inactive_amount', 'unredeemed_gift_card_total_amount', 'requiresPassword'],

    computed: {
        statusFilterValue() {
            const s = this.mainForm.status;
            if (s && typeof s === 'object' && 'value' in s) {
                return s.value;
            }
            return s;
        },

        /** Remaining balance applies to one-time dollar discounts (gift-style balance), not % or unlimited $ */
        showRemainingBalanceLine() {
            return (
                this.form.type === 'dollar_discount'
                && Number(this.form.usage) === 1
            );
        },

        /** Count of orders not shown after the capped order-history list. */
        outletOrdersRemaining() {
            const total = Number(this.form.outlet_orders_total) || 0;
            const shown = this.form.outlet_orders.length;
            return Math.max(0, total - shown);
        },
    },

    mounted() {
        if (this.requiresPassword) {
            this.$nextTick(() => {
                this.$refs.passwordInput?.focus();
            });
        } else {
            const params = new URLSearchParams(window.location.search);
            const id = parseInt(params.get('id'));
            let code = this.codes.data.find(code => code.id === id);

            if (code) {
                this.editPromoCode(code);
            }
        }
    },

    data() {
        return {
            showModal: false,
            showDeleteModal: false,

            outletOrdersCancel: null,

            paginateOptions: [
                { value: 10, label: 'Show 10' },
                { value: 20, label: 'Show 20' },
                { value: 50, label: 'Show 50' },
                { value: 100, label: 'Show 100' },
            ],
            statusOptions: [
                { value: 'all', label: 'All' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
            ],
            usageTypeOptions: [
                { value: 'all', label: 'All usages' },
                { value: 1, label: 'One time' },
                { value: 0, label: 'Unlimited' },
                { value: -1, label: 'Once per customer' },
                { value: 2, label: 'New customers only' },
            ],
            inactiveSubfilterOptions: [
                { value: 'all', label: 'All' },
                { value: 'deleted', label: 'Deleted' },
                { value: 'fully_consumed', label: 'Fully consumed' },
                { value: 'not_fully_consumed', label: 'Not fully consumed' },
            ],
            discountTypeOptions: [
                { value: 'all', label: 'All Types' },
                { value: 'prc_discount', label: '% Discount' },
                { value: 'dollar_discount', label: '$ Discount' },
            ],
        }
    },

    methods: {
        verifyPassword() {
            this.passwordForm.post(route('settings.promo_codes.verify-password'), {
                onError: (errors) => {
                    this.passwordForm.errors = errors;
                }
            });
        },

        handlePasswordCancel() {
            // Go back to previous page
            window.history.back();
        },

        normalizeFilterValue(val) {
            if (val && typeof val === 'object' && 'value' in val) {
                return val.value;
            }
            return val;
        },

        handleSubmit: _.debounce(function () {
            this.mainForm.status = this.normalizeFilterValue(this.mainForm.status);
            this.mainForm.inactive_subfilter = this.normalizeFilterValue(this.mainForm.inactive_subfilter);
            this.mainForm.discountType = this.normalizeFilterValue(this.mainForm.discountType);
            this.mainForm.usageType = this.normalizeFilterValue(this.mainForm.usageType);
            this.mainForm.get(route('settings.promo_codes.index'), { preserveState: true });
        }, 250),

        onStatusFilterChange() {
            const v = this.normalizeFilterValue(this.mainForm.status);
            if (v === 'active') {
                this.mainForm.inactive_subfilter = 'all';
            }
            this.handleSubmit();
        },

        openAddModal() {
            this.form.clearErrors();
            this.form.reset();
            this.form.is_active = true;
            this.form.created_for = '';
            this.showModal = 'add';
        },

        handleReset () {
            this.mainForm.search = '';
            this.mainForm.sortColumn = 'id';
            this.mainForm.sortDirection = 'asc';
            this.mainForm.status = 'active';
            this.mainForm.inactive_subfilter = 'all';
            this.mainForm.discountType = 'all';
            this.mainForm.usageType = 'all';
            this.handleSubmit();
        },

        closeModal() {
            this.form.reset();
            this.form.clearErrors();
            this.showModal = false;
        },

        editPromoCode(data) {
            if (this.outletOrdersCancel) this.outletOrdersCancel.cancel();
            this.outletOrdersCancel = axios.CancelToken.source();

            this.form.id = data.id;
            this.form.code = data.code;
            this.form.type = data.type;
            this.form.usage = data.usage;
            this.form.discount_amount = data.discount_amount;
            this.form.is_employee_code = data.is_employee_code;
            this.form.is_active = data.is_active;
            this.form.created_for = data.created_for || '';
            this.form.creation_type = data.creation_type;
            this.form.orig_discount_amount = data.orig_discount_amount;
            this.form.times_used = data.times_used;
            this.form.display_status = data.display_status;
            this.form.created_by_user = data.created_by_user;
            this.form.deleted_at = data.deleted_at;

            this.loadPromoCodeOrders(false);

            this.showModal = 'edit';
        },

        loadPromoCodeOrders(load_all = false) {
            this.form.loading_outlet_orders = true;
            this.form.outlet_orders_total = 0;
            axios.get(route('settings.promo_codes.getOrders', {id: this.form.id, load_all: load_all}), { cancelToken: this.outletOrdersCancel.token })
            .then(response => {
                this.form.outlet_orders = response.data.orders ?? [];
                this.form.outlet_orders_total = response.data.total ?? 0;
            })
            .catch(error => {
                console.error(error);
                this.form.outlet_orders = [];
                this.form.outlet_orders_total = 0;
                this.form.loading_outlet_orders = false;
            })
            .finally(() => {
                this.form.loading_outlet_orders = false;
            });
        },

        submitForm() {
            if (this.showModal === 'add') {
                this.form.transform((data) => ({
                    code: data.code,
                    type: data.type,
                    usage: data.usage,
                    discount_amount: data.discount_amount,
                    is_employee_code: data.is_employee_code,
                    is_active: data.is_active,
                    created_for: data.created_for,
                })).post(this.route('settings.promo_codes.store'), {
                    onSuccess: () => {
                        this.form.reset();
                        this.showModal = false;
                    }
                });
            } else {
                this.form.transform((data) => ({
                    id: data.id,
                    code: data.code,
                    type: data.type,
                    usage: data.usage,
                    discount_amount: data.discount_amount,
                    is_employee_code: data.is_employee_code,
                    is_active: data.is_active,
                    created_for: data.created_for,
                })).put(this.route('settings.promo_codes.update', this.form.id), {
                    onSuccess: () => this.closeModal()
                });
            }
        },

        deletePromoCode(data) {
            this.form.id = data.id;
            this.form.code = data.code;
            this.showDeleteModal = true;
        },

        handleDeletePromoCode() {
            this.form.delete(this.route('settings.promo_codes.destroy', this.form.id), {
                onSuccess: () => {
                    this.form.reset();
                    this.showModal = false;
                    this.showDeleteModal = false;
                }
            });
        },

        handleSort(sort) {
            this.mainForm.sortColumn = sort.column;
            this.mainForm.sortDirection = sort.direction;
            this.handleSubmit();
        },

        formatOrderPromoDiscount(amount) {
            return '$' + Number(amount).toFixed(2);
        },

        formatCreationTypeLabel(type) {
            if (!type) {
                return '';
            }
            const t = String(type).toLowerCase();
            if (t === 'auto') {
                return 'Auto';
            }
            if (t === 'manual') {
                return 'Manual';
            }
            return type;
        },

        formatOriginalDiscountDisplay(form) {
            if (form.orig_discount_amount == null) {
                return '';
            }
            const n = Number(form.orig_discount_amount);
            if (form.type === 'dollar_discount') {
                return '$ ' + n;
            }
            if (form.type === 'prc_discount') {
                return n + ' %';
            }
            return String(n);
        },

        orderUrl(id) {
            return route('orders.show', {
                order: id,
                return: route('settings.promo_codes.index', {
                    id: this.form.id,
                    paginate: this.mainForm.paginate,
                    search: this.mainForm.search,
                    sortColumn: this.mainForm.sortColumn,
                    sortDirection: this.mainForm.sortDirection,
                    status: this.statusFilterValue,
                    inactive_subfilter: this.mainForm.inactive_subfilter,
                    discountType: this.normalizeFilterValue(this.mainForm.discountType),
                    usageType: this.normalizeFilterValue(this.mainForm.usageType),
                }),
            });
        },
    },

    watch: {
        'form.is_employee_code'(newVal) {
            if (!newVal) {
            }
        }
    }
}
</script>

<style scoped>
</style>
